import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Box,
} from "@mui/material";
import PCheckbox from "components/PCheckbox";
import PSelect from "components/PSelect";
import { useFormik } from "formik";
import { CollectPermission, CollectUserRole } from "generated/graphql";
import useAppDispatchWithSnackbar from "helpers/hooks/useAppDispatchWithSnackbar";
import { useState } from "react";
import { useAppSelector } from "redux/hooks";
import { inviteCollectUser } from "redux/slices/client";
import { selectLenses } from "redux/slices/client";
import { P } from "theme/typography";
import { useAllowedFirms } from "helpers/hooks/useAllowedLenses";
import { CollectLogo } from "components/Icons";
import theme from "theme";
import Pendo from "helpers/Pendo";

interface FormValues {
  email: string;
  firstName: string;
  lastName: string;
  permissions: CollectPermission[];
  lensIds: number[];
  firmIds: number[];
  userRole: CollectUserRole;
}

const initialValues: FormValues = {
  email: "",
  firstName: "",
  lastName: "",
  permissions: [],
  lensIds: [],
  firmIds: [],
  userRole: CollectUserRole.Admin,
};

const CollectInvite = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const [isLoading, setIsLoading] = useState(false);
  const lenses = useAppSelector(selectLenses);
  const snackpatch = useAppDispatchWithSnackbar();

  const formik = useFormik({
    initialValues: initialValues,
    validateOnMount: true,
    validate: (values) => {
      const errors: any = {};
      if (!values.email) {
        errors.email = "Required";
      }
      if (!values.firstName) {
        errors.firstName = "Required";
      }
      if (!values.lastName) {
        errors.lastName = "Required";
      }
      if (!values.lensIds.length) {
        errors.lensIds = "Required";
      }
      if (!values.firmIds.length) {
        errors.firmIds = "Required";
      }
      return errors;
    },
    onSubmit: (values) => {
      setIsLoading(true);
      Pendo.event("invite_collect_user", {});
      void snackpatch(
        inviteCollectUser({
          email: values.email.toLowerCase().trim(),
          firstName: values.firstName.trim(),
          lastName: values.lastName.trim(),
          permissions: values.permissions,
          lensIds: values.lensIds,
          userRole: values.userRole,
          firmIds: values.firmIds,
        }),
        {
          successMessage: `Invite sent to ${values.email}`,
          errorMessage: "Error sending invite",
        }
      )
        .then(() => {
          formik.resetForm();
          onClose();
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
  });
  const allowedFirms = useAllowedFirms(formik.values.lensIds);

  return (
    <Dialog open={open}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "16px 24px",
          borderBottom: `1px solid ${theme.palette.grey[100]}`,
        }}>
        <P variant="xlBold">Invite Collect User</P>
        <CollectLogo size={32} />
      </Box>
      <DialogContent
        sx={{
          width: "500px",
        }}>
        <form onSubmit={formik.handleSubmit}>
          <Box
            sx={{
              "& > * + *": {
                marginTop: 2,
              },
            }}>
            <P
              variant="body2"
              sx={{
                mb: 1,
              }}>
              User Info
            </P>
            <TextField
              fullWidth
              label="Email"
              required
              name="email"
              value={formik.values.email}
              onChange={formik.handleChange}
            />
            {/* first name */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                "& > * + *": {
                  marginLeft: 1,
                },
              }}>
              <TextField
                fullWidth
                label="First Name"
                required
                name="firstName"
                value={formik.values.firstName}
                onChange={formik.handleChange}
              />
              {/* last name */}
              <TextField
                fullWidth
                label="Last Name"
                required
                name="lastName"
                value={formik.values.lastName}
                onChange={formik.handleChange}
              />
              {/* role */}
              <FormControl required fullWidth sx={{ ml: 1 }}>
                <InputLabel>Role</InputLabel>
                <PSelect
                  fullWidth
                  label="Role"
                  required
                  name="userRole"
                  value={formik.values.userRole}
                  onChange={formik.handleChange}>
                  <MenuItem value={CollectUserRole.Viewer}>Viewer</MenuItem>
                  <MenuItem value={CollectUserRole.Contributor}>Contributor</MenuItem>
                  <MenuItem value={CollectUserRole.Admin}>Admin</MenuItem>
                </PSelect>
              </FormControl>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}>
              <Box>
                <P
                  variant="body2"
                  sx={{
                    mb: 1,
                  }}>
                  Permissions
                </P>
                <Box
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    px: 2,
                  }}>
                  {Object.entries(CollectPermission).map(([key, value]) => (
                    <PCheckbox
                      key={key}
                      label={key}
                      checked={formik.values.permissions.includes(value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          formik.setFieldValue("permissions", [...formik.values.permissions, value]).catch((error) => {
                            console.error("CollectInvite; Error setting permissions checked", error);
                          });
                        } else {
                          formik
                            .setFieldValue(
                              "permissions",
                              formik.values.permissions.filter((p) => p !== value)
                            )
                            .catch((error) =>
                              console.error("CollectInvite; Error setting permissions unchecked", error)
                            );
                        }
                      }}
                      name={key}
                    />
                  ))}
                </Box>
              </Box>

              <Box>
                <P
                  variant="body2"
                  sx={{
                    mb: 1,
                  }}>
                  Lens Access
                </P>
                <Box
                  sx={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    px: 2,
                  }}>
                  {lenses.map((lens) => (
                    <Box
                      key={lens.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                      }}
                      onClick={() => {
                        // make the whole box clickable because the checkbox is too small
                        if (formik.values.lensIds.includes(lens.id)) {
                          formik
                            .setFieldValue(
                              "lensIds",
                              formik.values.lensIds.filter((id) => id !== lens.id)
                            )
                            .catch((error) =>
                              console.error("CollectInvite; Error removing lensId from collect user lenses", error)
                            );
                        } else {
                          formik.setFieldValue("lensIds", [...formik.values.lensIds, lens.id]).catch((error) => {
                            console.error("CollectInvite; Error adding lensId to collect user lenses", error);
                          });
                        }
                      }}>
                      <PCheckbox name="lensIds" value={lens.id} checked={formik.values.lensIds.includes(lens.id)} />
                      <P>{lens.name}</P>
                    </Box>
                  ))}
                </Box>
              </Box>
              {allowedFirms.length === 0 ? (
                <P variant="sm">No firms available for the selected lenses</P>
              ) : (
                <Box>
                  <P
                    variant="body2"
                    sx={{
                      mb: 1,
                    }}>
                    Firm Access
                  </P>
                  <Box
                    sx={{
                      maxHeight: "200px",
                      overflowY: "auto",
                      px: 2,
                    }}>
                    {allowedFirms.map((firm) => (
                      <Box
                        key={firm.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={() => {
                          // make the whole box clickable because the checkbox is too small
                          if (formik.values.firmIds.includes(firm.id)) {
                            formik
                              .setFieldValue(
                                "firmIds",
                                formik.values.firmIds.filter((id) => id !== firm.id)
                              )
                              .catch((error) =>
                                console.error("CollectInvite; Error removing firmId from collect user firms", error)
                              );
                          } else {
                            formik.setFieldValue("firmIds", [...formik.values.firmIds, firm.id]).catch((error) => {
                              console.error("CollectInvite; Error adding firmId to collect user firms", error);
                            });
                          }
                        }}>
                        <PCheckbox name="firmIds" value={firm.id} checked={formik.values.firmIds.includes(firm.id)} />
                        <P>{firm.name}</P>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </form>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={formik.submitForm} disabled={!formik.isValid || isLoading} variant="contained">
          Invite Collect {formik.values.userRole}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollectInvite;
